/**
 * Dựng các dòng chữ của một thông báo hết hạn mức từ `ThongTinHanMuc`, theo ngôn ngữ đang chọn.
 *
 * Tách khỏi component vì `useLoi` cũng cần: toast không thành khối được, chỉ hiện `dongGon`.
 */
import { useCallback } from 'react';

import { useT } from '../i18n';
import { dungNhanAi } from '../modules/nhat-ky-truy-vet/chung/nghiep-vu/loc-ai';
import { soVN } from '../lib/dinh-dang';
import { tachLoiHanMuc, type KyHanMuc, type ThongTinHanMuc } from '../lib/loi-han-muc';

export interface MoTaHanMuc {
  /** Việc nền không làm được, đứng trước tiêu đề hạn mức (bước dựng tiêu đề của tri thức). */
  boQua: string | null;
  tieuDe: string;
  tinhNang: string | null;
  daDung: string | null;
  /** Tỉ lệ đã dùng cho thanh, 0 tới 100. */
  phanTram: number | null;
  can: string | null;
  con: string | null;
  moLai: string | null;
  /** Dòng phụ của ô bảng: phạm vi đã ở tiêu đề hoặc `title`. */
  dongO: string | null;
  /** Hệ quả với lượt này. */
  ghiChu: string[];
  khacVanDung: string | null;
  /** Việc người dùng làm được tiếp. */
  huongDan: string | null;
}

const KHOA_KY = { ngay: 'hanMucAi.ky.ngay', tuan: 'hanMucAi.ky.tuan', thang: 'hanMucAi.ky.thang' } as const;

export function useMoTaHanMuc() {
  const { t, locale } = useT();

  const moTa = useCallback(
    (tt: ThongTinHanMuc): MoTaHanMuc => {
      const ky = t(KHOA_KY[(tt.ky ?? 'thang') as KyHanMuc]);
      const so = (n: number | null) => soVN(n, locale);
      const ten = tt.tenPhongBan ?? '';

      const tieuDe = () => {
        switch (tt.loai) {
          case 'het-goi':
            if (tt.viec) return t('hanMucAi.tieuDe.hetToChuc');
            return t(tt.biCat ? 'hanMucAi.tieuDe.hetGoiBiCat' : 'hanMucAi.tieuDe.hetGoi');
          case 'het-ky':
            return t('hanMucAi.tieuDe.hetKy');
          case 'vuot-phong-ban':
            return ten ? t('hanMucAi.tieuDe.vuotPhongBan', { ten, ky }) : t('hanMucAi.tieuDe.vuotPhongBanCuaBan', { ky });
          case 'vuot-nguoi':
            return t('hanMucAi.tieuDe.vuotNguoi', { ky });
          case 'khong-du':
            if (tt.dauKhongDu === 'am-thanh') return t('hanMucAi.tieuDe.khongDuBanGhi');
            return t(tt.dauKhongDu === 'dau-ra' ? 'hanMucAi.tieuDe.khongDuKetQua' : 'hanMucAi.tieuDe.khongDuNoiDung');
          case 'chua-kiem':
            return t('hanMucAi.tieuDe.chuaKiem');
        }
      };

      const con = () => {
        if (tt.con === null) return null;
        if (tt.phamVi === 'to-chuc') return t('hanMucAi.conToChuc', { so: so(tt.con) });
        if (tt.phamVi === 'nguoi') return t('hanMucAi.conNguoi', { so: so(tt.con), ky });
        return ten
          ? t('hanMucAi.conPhongBan', { ten, so: so(tt.con), ky })
          : t('hanMucAi.conPhongBanCuaBan', { so: so(tt.con), ky });
      };

      const huongDan = () => {
        const nenTang = tt.lienHe === 'nen-tang';
        if (tt.loai === 'khong-du' && tt.dauKhongDu === 'dau-vao') {
          return t(nenTang ? 'hanMucAi.goiYNoiDungNenTang' : 'hanMucAi.goiYNoiDungToChuc');
        }
        if (tt.loai === 'khong-du' && tt.dauKhongDu === 'am-thanh') {
          return t(nenTang ? 'hanMucAi.goiYBanGhiNenTang' : 'hanMucAi.goiYBanGhiToChuc');
        }
        if (tt.lienHe === 'nen-tang') return t('hanMucAi.lienHeNenTang');
        if (tt.lienHe === 'to-chuc') return t('hanMucAi.lienHeToChuc');
        if (tt.lienHe === 'quan-tri') return t('hanMucAi.lienHeQuanTri');
        return null;
      };

      const ghiChu: string[] = [];
      if (tt.biCat) ghiChu.push(t('hanMucAi.biCat'));
      if (tt.viec === 'chep-loi') ghiChu.push(t('hanMucAi.chuaChepLoi'));
      if (tt.viec === 'tom-tat') ghiChu.push(t('hanMucAi.chuaTomTat'));
      if (tt.banChepConDung) ghiChu.push(t('hanMucAi.banChepConDung'));
      if (tt.loai === 'chua-kiem') ghiChu.push(t(tt.heThongThuLai ? 'hanMucAi.heThongThuLai' : 'hanMucAi.chuaGoiDuocAi'));

      const coThanh = tt.daDung !== null && tt.hanMuc !== null && tt.hanMuc > 0;
      return {
        boQua: tt.boQuaTieuDe ? t('hanMucAi.boQuaTieuDe') : null,
        tieuDe: tieuDe(),
        tinhNang: tt.tinhNang ? t('hanMucAi.tinhNang', { ten: dungNhanAi(t).tinhNang(tt.tinhNang, null) }) : null,
        daDung: tt.daDung !== null && tt.hanMuc !== null ? t('hanMucAi.daDung', { daDung: so(tt.daDung), hanMuc: so(tt.hanMuc) }) : null,
        phanTram: coThanh ? Math.min(100, Math.round((tt.daDung! / tt.hanMuc!) * 100)) : null,
        can: tt.can === null ? null : t(tt.dauKhongDu === 'dau-ra' ? 'hanMucAi.canItNhat' : 'hanMucAi.can', { so: so(tt.can) }),
        con: con(),
        moLai: tt.moLaiGio && tt.moLaiNgay ? t('hanMucAi.moLai', { gio: tt.moLaiGio, ngay: tt.moLaiNgay }) : null,
        dongO: tt.moLaiGio && tt.moLaiNgay
          ? t('hanMucAi.moLaiNgan', { gio: tt.moLaiGio, ngay: tt.moLaiNgay })
          : tt.can !== null && tt.con !== null
            ? t('hanMucAi.canConNgan', { can: so(tt.can), con: so(tt.con) })
            : null,
        ghiChu,
        khacVanDung: tt.khacVanDung ? t('hanMucAi.khacVanDung') : null,
        huongDan: huongDan(),
      };
    },
    [t, locale],
  );

  /** Một dòng cho toast và dải lỗi hẹp: giữ đúng phạm vi và số, bỏ phần giải thích. */
  const dongGon = useCallback(
    (tt: ThongTinHanMuc): string => {
      const m = moTa(tt);
      return `${[m.boQua, m.tieuDe, m.can, m.con, m.moLai].filter(Boolean).join('. ')}.`;
    },
    [moTa],
  );

  /** Câu tới dưới dạng chuỗi và chỉ đặt được trên một dòng (toast, tooltip): câu hết hạn mức thì gọn, câu khác giữ nguyên. */
  const gonCau = useCallback(
    (cau: string): string => {
      const tt = tachLoiHanMuc(cau);
      return tt ? dongGon(tt) : cau;
    },
    [dongGon],
  );

  return { moTa, dongGon, gonCau };
}
